import Pickr from "@simonwep/pickr";
import { $, $$ } from "@utils/dom";
import { DATA_ATTR } from "@constants/data-attributes";
import { parseRGBString, toRGBCss } from "@utils/color";
import { applyCheckerboardPattern, clearCheckerboardPattern } from "@utils/dom";

/**
 * Initialize color picker functionality using Pickr
 */
export function initColorPickers(): void {
  const colorInputs = $$(DATA_ATTR.COLORPICKER);

  colorInputs.forEach((container) => {
    const input = $<HTMLInputElement>("input[type='hidden']", container);
    const trigger = $<HTMLElement>(DATA_ATTR.COLORPICKER_TRIGGER, container);
    const valueDisplay = $<HTMLSpanElement>("span", container);

    if (!input || !trigger) return;

    // Parse initial R,G,B value - can be empty
    const initialValue = input.value ? input.value.trim() : "";
    let initialColor: string | null = null;
    let hasColor = false;

    if (initialValue) {
      const color = parseRGBString(initialValue);
      if (color) {
        initialColor = toRGBCss(color);
        trigger.style.backgroundColor = initialColor;
        hasColor = true;
        if (valueDisplay) {
          valueDisplay.textContent = initialValue;
        }
      }
    }

    // Set empty state styling (checkerboard pattern)
    if (!hasColor) {
      trigger.style.backgroundColor = "";
      applyCheckerboardPattern(trigger);
      if (valueDisplay) {
        valueDisplay.textContent = "";
      }
    }

    const pickr = Pickr.create({
      el: trigger,
      theme: "nano",
      default: initialColor ?? "#ffffff",
      useAsButton: true,
      lockOpacity: true,
      padding: 15,
      position: "left-start",
      components: {
        preview: true,
        hue: true,
        interaction: {
          input: true,
          clear: true,
          save: true,
        },
      },
    });

    // Track if user has actively picked a color
    let userHasPicked = hasColor;

    // Helper to update values
    const updateColor = (color: Pickr.HSVaColor | null): void => {
      if (color && userHasPicked) {
        const rgba = color.toRGBA();
        const r = Math.round(rgba[0] ?? 0);
        const g = Math.round(rgba[1] ?? 0);
        const b = Math.round(rgba[2] ?? 0);
        const value = `${r},${g},${b}`;

        input.value = value;
        trigger.style.backgroundColor = `rgb(${r}, ${g}, ${b})`;
        clearCheckerboardPattern(trigger);
        if (valueDisplay) {
          valueDisplay.textContent = value;
        }
      }
    };

    // Helper to clear color
    const clearColor = (): void => {
      input.value = "";
      userHasPicked = false;
      trigger.style.backgroundColor = "";
      applyCheckerboardPattern(trigger);
      if (valueDisplay) {
        valueDisplay.textContent = "";
      }
    };

    // Update display while picking - marks as user picked
    pickr.on("change", (color: Pickr.HSVaColor) => {
      userHasPicked = true;
      updateColor(color);
    });

    // Handle clear button
    pickr.on("clear", () => {
      clearColor();
      pickr.hide();
    });

    // Handle save button
    pickr.on("save", (color: Pickr.HSVaColor | null) => {
      userHasPicked = true;
      updateColor(color);
      pickr.hide();
    });

    // Save when hidden (clicking outside)
    pickr.on("hide", () => {
      if (userHasPicked) {
        const color = pickr.getColor();
        updateColor(color);
      }
    });
  });
}
